import React from 'react';
import { cx, StatusChip } from '../ui';
import { gap } from '../content';
import { UNIT } from './data';
import { Hot, Icon, HalvardMark } from './parts';

type P = { step: number; acting: boolean; hot: boolean; onAct: () => void; hotLabel: string };

const INK = '#232047';
const MUTED = '#67648F';
const LINE = '#E4E3F0';

type Tab = 'Units' | 'Registrations' | 'Cases' | 'Boards';

function Shell({ tab, children }: { tab: Tab; children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-4 border-b px-4 sm:px-5" style={{ borderColor: LINE }}>
        <div className="py-3">
          <HalvardMark size={15} />
        </div>
        <nav className="flex h-full gap-4 text-[12.5px] font-medium" aria-hidden="true">
          {(['Units', 'Registrations', 'Cases', 'Boards'] as Tab[]).map((t) => (
            <span key={t} className={cx('relative py-3', t === tab ? 'text-[#241E61]' : 'text-[#67648F]')}>
              {t}
              {t === tab && <span className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-[#241E61]" />}
            </span>
          ))}
        </nav>
        <span className="ml-auto hidden h-7 w-7 place-items-center rounded-full text-[10px] font-bold text-white sm:grid" style={{ background: '#241E61' }}>
          KH
        </span>
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

function Pill({ children, tone = 'grey', pulse }: { children: React.ReactNode; tone?: 'grey' | 'green' | 'amber' | 'indigo'; pulse?: boolean }) {
  const s = {
    grey: { background: '#F1F0F7', color: MUTED },
    green: { background: '#E7F5EC', color: '#23794A' },
    amber: { background: '#FDF1DE', color: '#8A5A12' },
    indigo: { background: '#ECEBF7', color: '#241E61' },
  }[tone];
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-[3px] text-[10.5px] font-semibold" style={s}>
      {pulse && <span className="bmt-dot h-1.5 w-1.5 rounded-full" style={{ background: 'currentColor' }} />}
      {children}
    </span>
  );
}

function H({ t, sub, right }: { t: string; sub?: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-3 px-4 pb-3 pt-4 sm:px-5">
      <div>
        <div className="text-[15px] font-bold tracking-[-0.01em]" style={{ color: '#241E61' }}>{t}</div>
        {sub && <div className="text-[11.5px]" style={{ color: MUTED }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

// ---------- Units list (steps 1-2) ----------
function Units({ step }: P) {
  const rows: { s: string; stage: string; tone: 'grey' | 'green' | 'amber' | 'indigo'; with: string; me?: boolean }[] = [
    { s: 'KAW9-26-04171', stage: 'Registered', tone: 'green', with: 'Ashdown Renewables' },
    { s: 'KAW9-26-04175', stage: 'In transit', tone: 'grey', with: 'To Fenwick Plumbing & Heating' },
    { s: 'KAW9-26-04178', stage: 'Registered', tone: 'green', with: 'Brightwell Heating' },
    { s: 'KAW9-26-04180', stage: 'With installer', tone: 'indigo', with: 'Brightwell Heating' },
    { s: UNIT.serial, stage: step === 2 ? 'Commissioning' : 'Being installed', tone: step === 2 ? 'amber' : 'indigo', with: 'Brightwell Heating', me: true },
    { s: 'KAW9-26-04186', stage: 'Built', tone: 'grey', with: 'Halvard' },
  ];
  return (
    <Shell tab="Units">
      <H t="Halvard AW-9" sub="2026 builds" right={<span className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px]" style={{ boxShadow: `inset 0 0 0 1px ${LINE}`, color: MUTED }}><Icon.search size={12} />Serial, owner, installer</span>} />
      <div className="hidden grid-cols-[1.2fr_1fr_1.2fr] border-y px-5 py-2 text-[10.5px] font-semibold uppercase tracking-wider sm:grid" style={{ borderColor: LINE, color: MUTED, background: '#FAFAFD' }}>
        <span>Serial</span><span>Stage</span><span>With</span>
      </div>
      <ul>
        {rows.map((r) => (
          <li key={r.s} className={cx('grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5 border-b px-4 py-2.5 sm:grid-cols-[1.2fr_1fr_1.2fr] sm:px-5', r.me && 'bg-[#F7F7FC]')} style={{ borderColor: LINE }}>
            <span className="font-mono text-[11.5px] font-medium" style={{ color: INK }}>
              {r.s}
              {r.me && <span className="ml-2 font-display text-[10px] font-semibold text-[#2F9E58]">this one</span>}
            </span>
            <span className="justify-self-end sm:justify-self-start"><Pill tone={r.tone} pulse={r.me && step === 2}>{r.stage}</Pill></span>
            <span className="col-span-2 text-[11.5px] sm:col-span-1" style={{ color: MUTED }}>{r.with}</span>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

// ---------- Registrations (step 3) ----------
function Registrations({ hot, onAct, hotLabel }: P) {
  const old = [
    { s: 'KAW9-26-04178', o: '2 Mill Close', d: 'Brightwell Heating', when: '27 Feb' },
    { s: 'KAW9-26-04171', o: 'The Old Forge', d: 'Ashdown Renewables', when: '24 Feb' },
    { s: 'KAW9-26-04163', o: '9 Station Road', d: 'Fenwick Plumbing & Heating', when: '19 Feb' },
  ];
  return (
    <Shell tab="Registrations">
      <H t="Registrations" sub="Newest first" />
      <div className="px-3 sm:px-4">
        <Hot active={hot} label={hotLabel} onAct={onAct} radius={14} tip="bottom">
          <div className="bmt-rowin rounded-[14px] bg-white p-3.5" style={{ boxShadow: 'inset 0 0 0 1.5px #3FAE6C, 0 10px 30px -18px rgba(36,30,97,.45)' }}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[12px] font-semibold" style={{ color: INK }}>{UNIT.serial}</span>
              <Pill tone="green">Just now</Pill>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-[11.5px]">
              {[
                ['Owner', '14 Orchard Lane'],
                ['Installer', 'Brightwell Heating, credited'],
                ['Warranty', `From ${UNIT.handover}`],
                ['Commissioning', 'Signed PDF, 2 signatures'],
              ].map(([k, v]) => (
                <div key={k} className="leading-tight">
                  <div className="text-[10.5px]" style={{ color: MUTED }}>{k}</div>
                  <div className="font-semibold" style={{ color: INK }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </Hot>
      </div>
      <ul className="mt-3 border-t" style={{ borderColor: LINE }}>
        {old.map((r) => (
          <li key={r.s} className="grid grid-cols-[1fr_auto] gap-x-3 border-b px-4 py-2.5 text-[11.5px] sm:px-5" style={{ borderColor: LINE }}>
            <span className="font-mono font-medium" style={{ color: INK }}>{r.s}</span>
            <span style={{ color: MUTED }}>{r.when}</span>
            <span className="col-span-2" style={{ color: MUTED }}>{r.o} · installed by {r.d}</span>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

// ---------- Unit record (steps 4-7, 9) ----------
type Ev = { t: string; d: string; when: string; show: boolean; icon: React.ReactNode };
function UnitRecord({ step, acting }: P) {
  const transfer = step === 9;
  const facts: [string, string][] = transfer
    ? [
        ['Owner', acting ? 'The new owners, from today' : '14 Orchard Lane, since 2026'],
        ['Installer', 'Brightwell Heating'],
        ['History', '7 entries, kept with the unit'],
        ['Build', '2026, radiators'],
      ]
    : [
        ['Owner', '14 Orchard Lane'],
        ['Installer', 'Brightwell Heating'],
        ['Warranty', 'Active to 3 Mar 2028'],
        ['Build', '2026, radiators'],
      ];
  const events: Ev[] = transfer
    ? [
        { t: 'Ownership moved to the new owners', d: 'Confirmed by the sellers. History kept, their details not shared.', when: 'Now', show: acting, icon: <Icon.swap size={14} /> },
        { t: 'Serviced by Brightwell Heating', d: 'Annual service, signed off', when: 'Year 3', show: true, icon: <Icon.wrench size={14} /> },
        { t: `Case ${UNIT.caseNo} closed`, d: 'Fan motor replaced under warranty', when: 'Month 9', show: true, icon: <Icon.check size={14} /> },
        { t: 'Registered to 14 Orchard Lane', d: 'By Brightwell Heating at commissioning', when: UNIT.handover, show: true, icon: <Icon.user size={14} /> },
      ]
    : [
        { t: `Case ${UNIT.caseNo} opened from a scan`, d: 'E21, fan stopped · 2 photos', when: 'Now', show: step === 7 && acting, icon: <Icon.alert size={14} /> },
        { t: 'Warranty part matched', d: 'KD-FM-0920 fan motor assembly', when: 'Month 9', show: step > 6 || (step === 6 && acting), icon: <Icon.cog size={14} /> },
        { t: 'Question answered', d: 'E21 · Source: installation manual p.42', when: 'Month 9', show: step >= 5, icon: <Icon.chat size={14} /> },
        { t: 'Homeowner scanned the plate', d: '14 Orchard Lane, first visit', when: 'Day 1', show: step >= 4, icon: <Icon.phone size={14} /> },
        { t: 'Registered to 14 Orchard Lane', d: 'By Brightwell Heating, commissioning PDF sealed', when: UNIT.handover, show: true, icon: <Icon.user size={14} /> },
        { t: 'Delivered to Brightwell Heating', d: 'From the Halvard works', when: '12 Feb 2026', show: true, icon: <Icon.window size={14} /> },
      ];
  const fresh = (e: Ev, i: number) => i === 0 || e.when === 'Now';
  const shown = events.filter((e) => e.show);
  return (
    <Shell tab="Units">
      <H t={UNIT.serial} sub="Halvard AW-9 air source heat pump" right={<Pill tone={transfer && acting ? 'indigo' : 'green'}>{transfer ? (acting ? 'New owners' : 'In use') : step === 7 && acting ? 'Case open' : 'In use'}</Pill>} />
      <dl className="mx-4 grid grid-cols-2 gap-px overflow-hidden rounded-[12px] sm:mx-5" style={{ background: LINE, boxShadow: `0 0 0 1px ${LINE}` }}>
        {facts.map(([k, v]) => (
          <div key={k} className="bg-white px-3 py-2 leading-tight">
            <dt className="text-[10.5px]" style={{ color: MUTED }}>{k}</dt>
            <dd key={v} className="bmt-flash mt-0.5 text-[12px] font-semibold" style={{ color: INK }}>{v}</dd>
          </div>
        ))}
      </dl>
      <div className="px-4 pb-1.5 pt-4 text-[10.5px] font-semibold uppercase tracking-wider sm:px-5" style={{ color: MUTED }}>{transfer ? 'Owners and history' : 'Activity'}</div>
      <ol className="px-4 sm:px-5">
        {shown.map((e, i) => (
          <li key={e.t} className={cx('relative flex gap-3 pb-3', fresh(e, i) && e.when !== UNIT.handover && 'bmt-rowin')}>
            <span className="relative z-[1] grid h-7 w-7 shrink-0 place-items-center rounded-full" style={i === 0 && e.when !== UNIT.handover ? { background: '#241E61', color: '#fff' } : { background: '#F1F0F7', color: '#241E61' }}>
              {e.icon}
            </span>
            {i < shown.length - 1 && <span className="absolute left-[13.5px] top-7 h-[calc(100%-28px)] w-px" style={{ background: LINE }} />}
            <span className="min-w-0 flex-1 leading-tight">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-[12px] font-semibold" style={{ color: INK }}>{e.t}</span>
                <span className="shrink-0 text-[10.5px]" style={{ color: MUTED }}>{e.when}</span>
              </span>
              <span className="mt-0.5 block text-[11px]" style={{ color: MUTED }}>{e.d}</span>
            </span>
          </li>
        ))}
      </ol>
    </Shell>
  );
}

// ---------- Case + bulletin (step 8) ----------
function CaseView({ acting, hot, onAct, hotLabel }: P) {
  // gap.context, answered for this unit: the context that is usually missing.
  const vals = ['Halvard AW-9, 2026 build', '14 Orchard Lane', 'Active to 3 Mar 2028', 'Brightwell Heating, 3 Mar 2026', 'Commissioning record, E21 question', 'None, first owner'];
  const arrived = gap.context.map((k, i) => [k, vals[i]] as [string, string]);
  return (
    <Shell tab="Cases">
      <H t={`Case ${UNIT.caseNo} · E21, fan stopped`} sub="Routed to Brightwell Heating · opened from a scan" right={<Pill tone="green">Under warranty</Pill>} />
      <div className="grid gap-3 px-4 sm:grid-cols-[1.1fr_1fr] sm:px-5">
        <div className="rounded-[14px] p-3" style={{ boxShadow: `inset 0 0 0 1px ${LINE}` }}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Arrived with it</span>
            <span className="flex gap-1">
              {['96% 55%', '78% 40%'].map((pos) => (
                <span key={pos} className="block h-[30px] w-[40px] overflow-hidden rounded-[6px]">
                  <img src="/images/v2/gen/heatpump-commission-b-thumb.jpg" alt="" className="h-full w-full object-cover" style={{ objectPosition: pos, transform: 'scale(2.2)', transformOrigin: pos }} />
                </span>
              ))}
            </span>
          </div>
          <dl className="mt-2 space-y-1.5 text-[11px]">
            {arrived.map(([k, v]) => (
              <div key={k} className="flex items-start gap-1.5">
                <span className="mt-[1px] text-[#2F9E58]"><Icon.check size={12} /></span>
                <dt className="sr-only">{k}</dt>
                <dd className="leading-snug" style={{ color: INK }}><span style={{ color: MUTED }}>{k}: </span>{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-2.5 border-t pt-2 text-[10.5px] leading-snug" style={{ borderColor: LINE, color: MUTED }}>
            Without an identity, the first call is spent asking which model, which serial and who installed it.
          </div>
        </div>
        <div className="order-first rounded-[14px] p-3 sm:order-none" style={{ background: '#F7F7FC', boxShadow: `inset 0 0 0 1px ${LINE}` }}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Service bulletin</span>
            <span className="origin-right scale-[.85]"><StatusChip status="pilot" /></span>
          </div>
          <div className="mt-1.5 text-[13px] font-bold leading-tight" style={{ color: '#241E61' }}>Fan connector check</div>
          <div className="mt-1.5 space-y-1 text-[11px]" style={{ color: INK }}>
            <div><span style={{ color: MUTED }}>Serials </span><span className="font-mono text-[10.5px]">KAW9-26-03950 to 04300</span></div>
            <div><span style={{ color: MUTED }}>Goes to </span>homeowners and their installers</div>
          </div>
          <div className="mt-3">
            <Hot active={hot} label={hotLabel} onAct={onAct} radius={999} tip="none">
              <div className="flex h-[38px] items-center justify-center gap-1.5 rounded-full text-[12.5px] font-semibold" style={acting ? { background: '#E7F5EC', color: '#23794A' } : { background: '#241E61', color: '#fff' }}>
                {acting ? (
                  <>
                    <Icon.check size={14} /> Sent to 351 units
                  </>
                ) : (
                  <>
                    <Icon.bell size={14} /> Send to 351 units
                  </>
                )}
              </div>
            </Hot>
          </div>
        </div>
      </div>
    </Shell>
  );
}

// ---------- Passport (step 10) ----------
function PassportAdmin({ acting }: P) {
  const rows: [string, string, 'Public' | 'Recycler' | 'Authority'][] = [
    ['Manufacturer and model', 'Your product data', 'Public'],
    ['Energy label and EPREL entry', 'Your documents', 'Public'],
    ['Repair and spare parts', 'Your documents', 'Public'],
    ['Refrigerant and recovery', 'Your ERP', 'Recycler'],
    ['Materials by component', 'Your ERP', 'Recycler'],
    ['Declaration of conformity', 'Your documents', 'Authority'],
  ];
  return (
    <Shell tab="Units">
      <H t="Passport" sub={`${UNIT.serial} · on the same code · example fields`} right={<Pill tone="green">Published</Pill>} />
      <div className="hidden grid-cols-[1.4fr_1fr_auto] border-y px-5 py-2 text-[10.5px] font-semibold uppercase tracking-wider sm:grid" style={{ borderColor: LINE, color: MUTED, background: '#FAFAFD' }}>
        <span>Field</span><span>Comes from</span><span>Who sees it</span>
      </div>
      <ul>
        {rows.map(([f, src, vis]) => (
          <li key={f} className={cx('grid grid-cols-[1fr_auto] items-center gap-x-3 border-b px-4 py-2.5 text-[11.5px] sm:grid-cols-[1.4fr_1fr_auto] sm:px-5', vis === 'Recycler' && acting && 'bg-[#F7F7FC]')} style={{ borderColor: LINE }}>
            <span className="font-semibold" style={{ color: INK }}>{f}</span>
            <span className="order-3 col-span-2 sm:order-none sm:col-span-1" style={{ color: MUTED }}>{src}</span>
            <span className="justify-self-end"><Pill tone={vis === 'Public' ? 'grey' : vis === 'Recycler' ? 'green' : 'indigo'}>{vis}</Pill></span>
          </li>
        ))}
      </ul>
    </Shell>
  );
}

// ---------- End of life (step 11) ----------
function EndOfLifeAdmin({ acting }: P) {
  const events: Ev[] = [
    { t: 'Take-back booked', d: 'By the new owners · collection by Brightwell Heating', when: 'Now', show: acting, icon: <Icon.recycle size={14} /> },
    { t: 'Replacement unit registered', d: 'Same address, new serial', when: 'Year 18', show: true, icon: <Icon.home size={14} /> },
    { t: 'Serviced by Brightwell Heating', d: 'Annual service, signed off', when: 'Year 17', show: true, icon: <Icon.wrench size={14} /> },
    { t: 'Ownership moved to the new owners', d: 'With consent, history kept', when: 'Year 4', show: true, icon: <Icon.swap size={14} /> },
  ];
  const shown = events.filter((e) => e.show);
  return (
    <Shell tab="Units">
      <H t={UNIT.serial} sub="Halvard AW-9 · end of life" right={<Pill tone={acting ? 'indigo' : 'amber'}>{acting ? 'Take-back booked' : 'Being replaced'}</Pill>} />
      <div className="mx-4 rounded-[12px] p-3 sm:mx-5" style={{ background: '#F7F7FC', boxShadow: `inset 0 0 0 1px ${LINE}` }}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Goes to the recycler with the unit</span>
          <span className="origin-right scale-[.85]"><StatusChip status="pilot" /></span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]" style={{ color: INK }}>
          {['Materials by component', 'Refrigerant type and charge', 'Dismantling guide', 'Full service history'].map((t) => (
            <span key={t} className="flex items-center gap-1.5"><span className="text-[#2F9E58]"><Icon.check size={12} /></span>{t}</span>
          ))}
        </div>
      </div>
      <div className="px-4 pb-1.5 pt-4 text-[10.5px] font-semibold uppercase tracking-wider sm:px-5" style={{ color: MUTED }}>Activity</div>
      <ol className="px-4 sm:px-5">
        {shown.map((e, i) => (
          <li key={e.t} className={cx('relative flex gap-3 pb-3', e.when === 'Now' && 'bmt-rowin')}>
            <span className="relative z-[1] grid h-7 w-7 shrink-0 place-items-center rounded-full" style={i === 0 ? { background: '#241E61', color: '#fff' } : { background: '#F1F0F7', color: '#241E61' }}>
              {e.icon}
            </span>
            {i < shown.length - 1 && <span className="absolute left-[13.5px] top-7 h-[calc(100%-28px)] w-px" style={{ background: LINE }} />}
            <span className="min-w-0 flex-1 leading-tight">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-[12px] font-semibold" style={{ color: INK }}>{e.t}</span>
                <span className="shrink-0 text-[10.5px]" style={{ color: MUTED }}>{e.when}</span>
              </span>
              <span className="mt-0.5 block text-[11px]" style={{ color: MUTED }}>{e.d}</span>
            </span>
          </li>
        ))}
      </ol>
    </Shell>
  );
}

// ---------- Boards (step 12): every unit at once, and the feedback loop ----------
const WEEKS = [31, 34, 30, 38, 41, 39, 44, 47, 52, 49, 55, 61];
const TOPICS: { t: string; n: number; s: string; flag?: boolean }[] = [
  { t: 'Flow temperature and radiators', n: 412, s: 'Answered from the installation manual' },
  { t: 'Frost protection when away', n: 12, s: 'No answer in your documents', flag: true },
  { t: 'E21, fan stopped', n: 38, s: 'Bulletin sent to 351 units' },
  { t: 'Annual service booking', n: 121, s: 'Handed to installers' },
];
function Boards({ acting, hot, onAct, hotLabel }: P) {
  const max = Math.max(...WEEKS);
  return (
    <Shell tab="Boards">
      <H t="Halvard AW-9 · last 90 days" sub="Every unit in the field, on one board" right={<Pill tone="green">Live</Pill>} />
      <div className="grid grid-cols-3 gap-2 px-4 sm:px-5">
        {[
          ['Registered', '3,846', '+212 this month'],
          ['Answered without a call', '82%', 'Each with its source'],
          ['Open cases', '14', 'All with full history'],
        ].map(([k, v, d]) => (
          <div key={k} className="rounded-[12px] p-2.5" style={{ boxShadow: `inset 0 0 0 1px ${LINE}` }}>
            <div className="truncate text-[10px]" style={{ color: MUTED }}>{k}</div>
            <div className="text-[19px] font-bold leading-tight tracking-[-0.02em]" style={{ color: '#241E61' }}>{v}</div>
            <div className="truncate text-[9.5px]" style={{ color: k === 'Registered' ? '#23794A' : MUTED }}>{d}</div>
          </div>
        ))}
      </div>
      <div className="mx-4 mt-2 rounded-[12px] p-2.5 sm:mx-5" style={{ boxShadow: `inset 0 0 0 1px ${LINE}` }}>
        <div className="text-[10px] font-semibold" style={{ color: MUTED }}>Registrations, last 12 weeks</div>
        <div className="mt-1.5 flex h-[46px] items-end gap-[3px]" aria-hidden="true">
          {WEEKS.map((n, i) => (
            <i key={i} className="block flex-1 rounded-[3px]" style={{ height: `${(n / max) * 100}%`, background: i === WEEKS.length - 1 ? '#2F9E58' : '#D9D7EE' }} />
          ))}
        </div>
      </div>
      <div className="px-4 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-wider sm:px-5" style={{ color: MUTED }}>What owners asked</div>
      <ul className="px-4 sm:px-5">
        {TOPICS.map((t) => {
          const done = t.flag && acting;
          return (
            <li key={t.t} className="flex items-center gap-2 border-t py-1.5 first:border-t-0" style={{ borderColor: '#EEEDF5' }}>
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block truncate text-[11.5px] font-semibold" style={{ color: INK }}>{t.t}</span>
                <span className="block truncate text-[10px]" style={{ color: done ? '#23794A' : t.flag ? '#8A5A12' : MUTED }}>{done ? 'Answer added, live for every AW-9' : t.s}</span>
              </span>
              <span className="text-[11.5px] font-semibold tabular-nums" style={{ color: INK }}>{t.n}</span>
            </li>
          );
        })}
      </ul>
      <div className="mx-4 mt-2 flex items-center gap-3 rounded-[12px] p-2.5 sm:mx-5" style={{ background: '#241E61' }}>
        <span className="min-w-0 flex-1 text-[11px] leading-snug text-white/85">
          <span className="block text-[9.5px] font-semibold uppercase tracking-wider text-[#CFF710]">Recommended</span>
          12 owners asked about frost protection. Your documents have no answer.
        </span>
        <Hot active={hot} label={hotLabel} onAct={onAct} radius={999} tip="none">
          <span className="flex h-[32px] items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[11.5px] font-semibold" style={acting ? { background: '#E7F5EC', color: '#23794A' } : { background: '#fff', color: '#241E61' }}>
            {acting ? <><Icon.check size={13} /> Added</> : 'Add an answer'}
          </span>
        </Hot>
      </div>
    </Shell>
  );
}

export function AdminScreen(props: P) {
  const { step } = props;
  const screen =
    step <= 2 ? <Units {...props} /> :
    step === 3 ? <Registrations {...props} /> :
    step === 8 ? <CaseView {...props} /> :
    step === 10 ? <PassportAdmin {...props} /> :
    step === 11 ? <EndOfLifeAdmin {...props} /> :
    step === 12 ? <Boards {...props} /> :
    <UnitRecord {...props} />;
  const key = step <= 2 ? 'units' : step >= 4 && step <= 7 ? 'record' : `s${step}`;
  return (
    <div key={key} className="bmt-screen h-full">
      {screen}
    </div>
  );
}
